import React, { useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
    PageTitle,
    Loading,
    Input,
    Modal,
    Button
} from '../root/components/common'
import { useSelector, useDispatch } from 'react-redux'
import { useQueryClient } from 'react-query'
import * as actions from './actions'
import { isEmpty } from 'lodash'
import { getFormattedDate } from '../../utils'
import { IconButton } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/DeleteOutline'
import ViewIcon from '@material-ui/icons/VisibilityOutlined'
import CopyIcon from '@material-ui/icons/FileCopyOutlined'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import LaunchIcon from '@material-ui/icons/Launch'
import QRCode from 'react-qr-code'
import * as clipboard from 'clipboard-polyfill/text'
import { enqueueSnackbar } from '../root/actions'
import { UserPermissions } from 'web/src/services/permissions'
import { useBillingConfirmation } from '../integrations/hooks/use-billing-confirmation'
import { INTEGRATIONS_AND_API_DATA_KEY } from '../integrations/hooks/use-integrations-and-api-data'
import BillingInfoModal from '../integrations/billing-info-modal'

const useStyles = makeStyles(theme => ({
    wrapper: {
        height: 'calc(100% - 70px)'
    },
    pageTitle: {
        justifyContent: 'space-between'
    },
    headerBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
        gap: 16
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 10
    },
    docsLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: 'none',
        border: 'none',
        padding: 0,
        color: theme.primary,
        fontSize: 13,
        fontWeight: 'bold',
        fontFamily: theme.fontFamily,
        textDecoration: 'underline',
        cursor: 'pointer',
        '&:hover': {
            opacity: 0.6
        }
    },
    link: {
        fontWeight: 'bold',
        cursor: 'pointer',
        marginTop: 18
    },
    descriptionInput: {
        marginTop: 16
    },
    modalButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 16
    },
    apiKeyWrapper: {
        padding: 24,
        marginBottom: 16,
        border: theme.border,
        borderRadius: theme.borderRadius,
        display: 'flex',
        alignItems: 'center'
    },
    keyNumber: {
        flex: 1,
        paddingRight: 8
    },
    keyDescription: {
        flex: 4,
        paddingRight: 8
    },
    keyMask: {
        flex: 4,
        paddingRight: 8
    },
    keyExpires: {
        flex: 4,
        paddingRight: 8
    },
    keyActions: {
        minWidth: 76,
        flex: 1,
        paddingRight: 8
    },
    viewButton: {
        marginRight: 8
    },
    deleteButton: {
        color: theme.error
    },
    apiKeyValueWrapper: {
        marginTop: 24,
        marginBottom: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    apiKeyValue: {
        width: '100%',
        marginRight: 8
    },
    qrCodeWrapper: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: 36
    },
    noKeysContainer: {
        display: 'flex',
        alignItems: 'center',
        marginTop: 36,
        flexDirection: 'column'
    }
}))

export function ApiKeysContent() {
    if (!UserPermissions.isAdmin()) return null
    const classes = useStyles()
    const dispatch = useDispatch()
    const queryClient = useQueryClient()
    const billingConfirmation = useBillingConfirmation()

    const { isLoading, locale } = useSelector(state => state.main)
    const { apiKeys, apiKey } = useSelector(state => state.apiKeys)

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [descriptionOfNew, setDescriptionOfNew] = useState('')
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [currentIdToDelete, setCurrentIdToDelete] = useState(null)
    const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false)

    const apiKeysCount = apiKeys?.length ?? 0
    const invalidateOnNextCountChangeRef = useRef(false)
    useEffect(() => {
        if (!invalidateOnNextCountChangeRef.current) return
        invalidateOnNextCountChangeRef.current = false
        queryClient.invalidateQueries(INTEGRATIONS_AND_API_DATA_KEY)
    }, [apiKeysCount, queryClient])

    const onCreate = () => {
        setDescriptionOfNew('')
        setIsCreateModalOpen(true)
    }

    const create = description => {
        setIsCreateModalOpen(false)
        billingConfirmation.requestConfirmation(() => {
            invalidateOnNextCountChangeRef.current = true
            dispatch(actions.createApiKey({ description }))
        })
    }

    const onDelete = id => {
        setCurrentIdToDelete(id)
        setIsDeleteModalOpen(true)
    }

    const deleteApiKey = id => {
        invalidateOnNextCountChangeRef.current = true
        dispatch(actions.deleteApiKey(id))
        setIsDeleteModalOpen(false)
    }

    const onView = id => {
        dispatch(actions.apiKeyRequest(id))
        setIsApiKeyModalOpen(true)
    }

    const setIsApiKeyModalOpenCustom = value => {
        setIsApiKeyModalOpen(value)
        dispatch(actions.apiKeySuccess(null))
    }

    const copyApiKeyToClipboard = async value => {
        try {
            await clipboard.writeText(value)
            dispatch(
                enqueueSnackbar({
                    message: locale.apiKeys.copied,
                    options: {
                        variant: 'success'
                    }
                })
            )
        } catch (ex) {
            dispatch(
                enqueueSnackbar({
                    message: locale.apiKeys.failedToCopy,
                    options: {
                        variant: 'warning'
                    }
                })
            )
        }
    }

    useEffect(() => {
        dispatch(actions.apiKeysRequest())
    }, [])

    return (
        <div>
            <div className={classes.headerBar}>
                <div className={classes.headerLeft}>
                    <a
                        className={classes.docsLink}
                        href="https://docs.traplinked.com/rest/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <HelpOutlineIcon style={{ fontSize: 16 }} />
                        {locale.apiKeys.viewDocs}
                        <LaunchIcon style={{ fontSize: 14 }} />
                    </a>
                </div>
                {!isEmpty(apiKeys) && (
                    <Button onClick={onCreate}>
                        {locale.apiKeys.createNew}
                    </Button>
                )}
            </div>

            {isLoading && <Loading />}

            {!isLoading && !isEmpty(apiKeys) && (
                <div>
                    {apiKeys.map((apiKey, idx) => {
                        return (
                            <div
                                key={apiKey.id}
                                className={classes.apiKeyWrapper}
                            >
                                <div className={classes.keyNumber}>
                                    {`${idx + 1}.`}
                                </div>
                                <div className={classes.keyDescription}>
                                    {`${
                                        locale.common.description
                                    }: ${apiKey.description || '--'}`}
                                </div>
                                <div className={classes.keyMask}>
                                    &#65290;&#65290;&#65290;&#65290;&#65290;&#65290;&#65290;&#65290;&#65290;&#65290;&#65290;&#65290;&#65290;
                                </div>
                                <div className={classes.keyExpires}>
                                    {locale.apiKeys.expires}:{' '}
                                    {getFormattedDate(apiKey.expires_at)}
                                </div>
                                <div className={classes.keyActions}>
                                    <IconButton
                                        className={classes.viewButton}
                                        size="small"
                                        onClick={onView.bind(this, apiKey.id)}
                                    >
                                        <ViewIcon />
                                    </IconButton>
                                    <IconButton
                                        className={classes.deleteButton}
                                        size="small"
                                        onClick={onDelete.bind(this, apiKey.id)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {!isLoading && isEmpty(apiKeys) && (
                <div className={classes.noKeysContainer}>
                    <div>{locale.apiKeys.noApiKeysFound}</div>
                    <div onClick={onCreate} className={classes.link}>
                        {locale.apiKeys.createNew}
                    </div>
                </div>
            )}

            <Modal open={isCreateModalOpen} setOpen={setIsCreateModalOpen}>
                <React.Fragment>
                    <div>{locale.apiKeys.plsProvideDescr}:</div>
                    <div>
                        <Input
                            autoFocus
                            className={classes.descriptionInput}
                            label={locale.common.description}
                            type="text"
                            setValue={setDescriptionOfNew}
                        />
                    </div>
                    <div className={classes.modalButtons}>
                        <Button onClick={create.bind(this, descriptionOfNew)}>
                            {locale.common.ok}
                        </Button>
                        <Button
                            reverted
                            onClick={setIsCreateModalOpen.bind(this, false)}
                        >
                            {locale.common.cancel}
                        </Button>
                    </div>
                </React.Fragment>
            </Modal>

            <Modal open={isDeleteModalOpen} setOpen={setIsDeleteModalOpen}>
                <React.Fragment>
                    <div>{locale.apiKeys.deleteKeyDescr}</div>
                    <div className={classes.modalButtons}>
                        <Button
                            onClick={deleteApiKey.bind(this, currentIdToDelete)}
                        >
                            {locale.common.ok}
                        </Button>
                        <Button
                            reverted
                            onClick={setIsDeleteModalOpen.bind(this, false)}
                        >
                            {locale.common.cancel}
                        </Button>
                    </div>
                </React.Fragment>
            </Modal>

            <Modal
                open={isApiKeyModalOpen && apiKey !== null}
                setOpen={setIsApiKeyModalOpenCustom}
            >
                <React.Fragment>
                    <div>
                        {apiKey && (
                            <React.Fragment>
                                <div>{locale.apiKeys.viewKeyDescr}</div>
                                <div className={classes.apiKeyValueWrapper}>
                                    <div className={classes.apiKeyValue}>
                                        <Input
                                            className={classes.apiKeyValue}
                                            type="text"
                                            disabled
                                            value={apiKey.api_key}
                                        />
                                    </div>
                                    <div>
                                        <IconButton
                                            size="small"
                                            onClick={copyApiKeyToClipboard.bind(
                                                this,
                                                apiKey.api_key
                                            )}
                                        >
                                            <CopyIcon />
                                        </IconButton>
                                    </div>
                                </div>
                                <div className={classes.qrCodeWrapper}>
                                    <QRCode value={apiKey.api_key} />
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className={classes.modalButtons}>
                        <Button
                            onClick={setIsApiKeyModalOpenCustom.bind(
                                this,
                                false
                            )}
                        >
                            {locale.common.ok}
                        </Button>
                        <Button
                            reverted
                            onClick={setIsApiKeyModalOpenCustom.bind(
                                this,
                                false
                            )}
                        >
                            {locale.common.cancel}
                        </Button>
                    </div>
                </React.Fragment>
            </Modal>

            <BillingInfoModal
                open={billingConfirmation.isOpen}
                onConfirm={billingConfirmation.confirm}
                onCancel={billingConfirmation.cancel}
            />
        </div>
    )
}

export default function ApiKeys() {
    const classes = useStyles()
    const { locale } = useSelector(state => state.main)

    if (!UserPermissions.isAdmin()) return null

    return (
        <div className={classes.wrapper}>
            <PageTitle
                title={locale.apiKeys.apiKeys}
                className={classes.pageTitle}
            />
            <ApiKeysContent />
        </div>
    )
}
